import { useCallback } from "react";
import capitalize from "lodash-es/capitalize";

import { Action, Module, Run, RunType, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const PROMOTE_ACCESS_TYPES = [Action.RunPromote];

const hasPromoteAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(PROMOTE_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook to determine if the current user can promote a run.
 *
 * Promotion is only possible for finished proposed runs.
 * Users need either:
 * - Stack/Module Write access
 * - RunPromote permission
 *
 * @returns A function that takes a run and entity (Stack or Module) and returns an AccessResult
 */
const useCanPromoteRun = (): ((
  run: Run,
  entity: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>
) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasEntityWriteAccess = useHasEntityWriteAccess();

  return useCallback(
    (run: Run, entity: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>) => {
      const isProposedRun = run.type === RunType.Proposed;
      if (!run.finished || !isProposedRun) {
        return {
          result: false,
        };
      }

      if (run.promoteBlocker) {
        return {
          result: false,
          message: capitalize(run.promoteBlocker),
        };
      }

      const hasAccess =
        hasEntityWriteAccess(entity) ||
        hasPromoteAccess(viewer, entity.spaceDetails?.id || entity.space);

      if (hasAccess) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: NO_ACCESS_MESSAGE,
      };
    },
    [viewer, hasEntityWriteAccess]
  );
};

export default useCanPromoteRun;
